import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  APIClients,
  ApiClientType,
  AuthenticatorUser,
  IntegrationsAPIClient,
  isAuth0Authenticated,
  isSageIDUser
} from '@futrli/shared-services';

import {
  getCurrentAuthenticatedUser,
  setCurrentAuthenticatedUserFulfilled,
  setCurrentAuthenticatedUserPending,
  setCurrentAuthenticatedUserRejected
} from '../redux-utils/actions';
import { matchesCurrentUserProfile, watchCurrentUserCacheReset } from '../service';
import { signOut } from '../../login/redux-utils/actions';
import { StorageBackedNumber } from '../storage';

interface AuthBootstrapOptions {
  lastActivityStorage: StorageBackedNumber;
  sessionDurationMs: number;
}

/**
 * Selects the authenticated user's provider for both API client collections.
 */
const configureApiClientsForUser = (user: AuthenticatorUser) => {
  const clientType = isSageIDUser(user) || isAuth0Authenticated() ? ApiClientType.Auth0 : ApiClientType.Aws;
  IntegrationsAPIClient.setApiClient(clientType);
  APIClients.setApiClient(clientType);
};

/**
 * Restores authentication on mount using the initial session timeout settings.
 * Signs out expired sessions; otherwise loads the user, configures API clients and synchronizes Redux.
 * Ignores abandoned-session responses and cleans up its cache-reset listener on unmount.
 * Ongoing activity tracking and expiry remain the responsibility of SessionTimeoutManager.
 */
export const useAuthBootstrap = ({ lastActivityStorage, sessionDurationMs }: AuthBootstrapOptions) => {
  const dispatch = useDispatch();
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const lastActivity = lastActivityStorage.getVal() || Date.now();
    const isExpired = lastActivity + sessionDurationMs < Date.now();

    // If this session should be expired already, force sign-out.
    if (isExpired) {
      dispatch(signOut());
      lastActivityStorage.setVal(null);
      setIsLoadingData(false);
      return;
    }

    let active = true;
    const cacheReset = watchCurrentUserCacheReset();

    /**
     * Allows bootstrap side effects only while mounted and consistent with the current session.
     */
    const canCompleteAuthentication = (user: AuthenticatorUser) => {
      if (!active) {
        return false;
      }
      // After a cache reset, a late SDK result must match the profile of the current session.
      return !cacheReset.hasBeenReset() || matchesCurrentUserProfile(user);
    };

    /**
     * Loads the authenticated user and completes bootstrap only if this attempt is still valid.
     */
    const checkAuthenticatedUser = async () => {
      try {
        dispatch(setCurrentAuthenticatedUserPending());
        const user = await dispatch(getCurrentAuthenticatedUser());
        if (!canCompleteAuthentication(user)) {
          return;
        }

        configureApiClientsForUser(user);
        dispatch(setCurrentAuthenticatedUserFulfilled(user));
      } catch {
        // An old request's failure must not reject a newer login or update an unmounted container.
        if (active && !cacheReset.hasBeenReset()) {
          dispatch(setCurrentAuthenticatedUserRejected());
        }
      } finally {
        if (active) {
          setIsLoadingData(false);
        }
      }
    };
    checkAuthenticatedUser();

    return () => {
      active = false;
      cacheReset.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { isLoadingData };
};
